import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, Send, X, ChevronDown, ChevronUp, Heart } from 'lucide-react';
import TextareaAutosize from 'react-textarea-autosize';
import { motion, AnimatePresence } from 'framer-motion';
import { faqData, getRandomFallback } from '../../services/chatbot/faqData';
import { chatAnalytics } from '../../services/chatbot/analytics';
import { nlpService } from '../../services/chatbot/nlpService';
import type { ChatMessage as ChatMessageType } from '../../types/chatbot';
import ChatMessage from './ChatMessage';

interface ChatBotProps {
  onClose: () => void;
}

export default function ChatBot({ onClose }: ChatBotProps) {
  const [isMinimized, setIsMinimized] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<ChatMessageType[]>([
    {
      id: 'welcome',
      type: 'bot',
      content: '👋 Hi! I\'m IPPA, your Intelligent Personal Patient Assistant. I\'m here to help you navigate your healthcare journey with compassion and expertise. How can I assist you today?',
      timestamp: new Date(),
      links: [
        { text: 'Find a Doctor', url: '/providers' },
        { text: 'Check Symptoms', url: '/#symptom-checker' }
      ]
    }
  ]);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;

    chatAnalytics.trackEvent('message', { type: 'user', content: message });

    const userMessage: ChatMessageType = {
      id: `user-${Date.now()}`,
      type: 'user',
      content: message,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setMessage('');

    // Process the message using NLP service
    const { answer, confidence, related } = nlpService.findAnswer(message);
    
    setTimeout(() => {
      const botMessage: ChatMessageType = answer ? {
        id: `bot-${Date.now()}`,
        type: 'bot',
        content: answer.answer,
        timestamp: new Date(),
        confidence,
        links: answer.links,
        callToAction: answer.callToAction,
        relatedQuestions: related
      } : {
        id: `bot-${Date.now()}`,
        type: 'bot',
        content: getRandomFallback(),
        timestamp: new Date(),
        callToAction: {
          text: 'Speak to Support',
          number: '(628) 333-5116'
        }
      };

      setMessages(prev => [...prev, botMessage]);
      chatAnalytics.trackEvent('message', { type: 'bot', content: botMessage.content });
    }, 1000);
  };

  const handleFeedback = (messageId: string, helpful: boolean) => {
    chatAnalytics.trackEvent('feedback', { messageId, helpful });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ 
        opacity: 1,
        y: 0,
        scale: 1,
        height: isMinimized ? 'auto' : '600px'
      }}
      exit={{ opacity: 0, y: 20, scale: 0.95 }}
      transition={{ duration: 0.2 }}
      className="fixed bottom-20 right-4 w-full sm:w-[400px] bg-white rounded-xl shadow-xl z-50 overflow-hidden"
    >
      {/* Persistent Header with Controls */}
      <div className="sticky top-0 z-10 p-4 bg-mint-600 text-white">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Heart className="w-5 h-5" />
            <h3 className="font-medium">IPPA Assistant</h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="p-2 hover:bg-mint-500 rounded-lg transition-colors"
              aria-label={isMinimized ? "Expand chat" : "Minimize chat"}
            >
              {isMinimized ? (
                <ChevronUp className="w-5 h-5" />
              ) : (
                <ChevronDown className="w-5 h-5" />
              )}
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-mint-500 rounded-lg transition-colors"
              aria-label="Close chat"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        {!isMinimized && (
          <p className="text-sm text-mint-100 mt-2">
            Your Intelligent Personal Patient Assistant
          </p>
        )}
      </div>

      {/* Chat Content */}
      <AnimatePresence>
        {!isMinimized && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            className="flex flex-col h-[calc(600px-144px)]"
          >
            <div
              ref={chatContainerRef}
              className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar"
            >
              {messages.map((msg) => (
                <ChatMessage
                  key={msg.id}
                  message={msg}
                  onFeedback={handleFeedback}
                />
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <form onSubmit={handleSubmit} className="sticky bottom-0 p-4 border-t bg-white">
              <div className="flex gap-2">
                <TextareaAutosize
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type your message to IPPA..."
                  className="flex-1 resize-none rounded-lg border-gray-300 focus:border-mint-500 focus:ring-mint-500"
                  maxRows={4}
                />
                <button
                  type="submit"
                  disabled={!message.trim()}
                  className="p-2 bg-mint-600 text-white rounded-lg hover:bg-mint-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
